import React, { useState, useEffect } from "react";
import { useAppSelector } from "../../../context-store";
import CompanyClasses from "../../../landing-pages/Company/company.module.css";
import companyDashboardService from "../../../services/company/dashboard-service";
import JobPostingSearchInputModel from "../../../models/company/jobPosting/request/JobPostingSearchInputModel";
import { SortDirection } from "../../../models/enums/SortDirectionEnum";
import PagedListModel from "../../../models/common/PagedListModel";
import modalUtils from "../../../utils/modal-utils";
import toasterUtils from "../../../utils/toaster-utils";
import CompanyDashboardModel from "../../../models/company/dashboard/response/CompanyDashboardModel";
import IdNameModel from "../../../models/common/IdNameModel";
import masterDataService from "../../../services/master-data-service";
import { ScreenEnum } from "../../../models/enums/StatusEnum";
import JobPostingDashboardGridComponent from "./JobPostingDashboardGridComponent";
import JobPostingStatusUpdateModel from "../../../models/company/jobPosting/request/JobPostingStatusUpdateModel";
import { GeneralPoupConfig, ModalSize } from "../../../models/modal-types";
import JobPostingStatusUpdatePopUp from "./JobPostingStatusUpdateComponent";
import jobPostingService from "../../../services/company/job-posting-service";
import { PagingEnum } from "../../../models/enums/IAIEnum";
import { FormControl, InputLabel, MenuItem, Select, Button, Autocomplete, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { GuidEnum } from "../../../models/enums/GuidEnum";

const JobPostingDashboardComponent = () => {
    let loggedInUser = useAppSelector((state) => state.authState.loggedInUser);
    const [jobPostingPagedList, setJobPostingPagedList] = useState<PagedListModel<CompanyDashboardModel>>();
    const [jobPostingList, setJobPostingList] = useState<CompanyDashboardModel[]>([]);
    const [primarySkills, setPrimarySkills] = useState<IdNameModel[]>([]);
    const [status, setStatus] = useState<IdNameModel[]>([]);
    const [recruiterList, setRecruiterList] = useState<IdNameModel[]>([]);
    const [refreshJobPosting, setRefreshJobPosting] = useState<number>(2);
    const [primarySkillSearch, setPrimarySkillSearch] = useState<number>(0);
    const [jobCodeSearch, setJobCodeSearch] = useState<string>("All");
    const [statusSearch, setStatusSearch] = useState<number>(14);
    const [page, setPage] = useState<number>(PagingEnum.page);
    const [pageSize, setPageSize] = useState<number>(PagingEnum.pageSize);
    const [sortColumn, setSortColumn] = useState<string>("CreatedDate");
    const [sortDirection, setSortDirection] = useState<number>(PagingEnum.sortDirectionDesc);
    const [pageSizesList, setPageSizesList] = useState<number[]>([]);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [escalationStatusList, setEscalationStatusList] = useState<IdNameModel[]>([]);
    const [refreshEscalationStatus, setRefreshEscalationStatus] = useState<number>(2);

    useEffect(() => {
        loadPageSizes();
        fillPrimarySkills();
        fillStatus();
        fillRecruiters();
        getJobPostings(getSearchFilters(page, pageSize, sortColumn, sortDirection));
        getEscalationStatusList();
    }, []);

    const fillPrimarySkills = () => {
        masterDataService.loadPrimarySkills().then((res) => {
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setPrimarySkills(res.items);
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    const fillStatus = () => {
        masterDataService.loadStatusByScreen(ScreenEnum.JobPostingStatus).then((res) => {
            setStatus(res.items);
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    const fillRecruiters = () => {
        if (loggedInUser?.profileId) {
            companyDashboardService.getRecruitersForCompany(loggedInUser.profileId).then((res) => {
                if (!res.isSuccess || res.errorMessages.length > 0) {
                    toasterUtils.showError(res.errorMessages.toString());
                    return;
                }
                setRecruiterList(res.items);
            }).catch((error) => {
                toasterUtils.showError(error);
            });
        }
    };

    const loadPageSizes = () => {
        masterDataService.loadPageSizesForPaging().then((res) => {
            setPageSizesList(res);
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    const getEscalationStatusList = () => {
        masterDataService.loadStatusByScreen(ScreenEnum.EscalationsStatus).then((res) => {
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setEscalationStatusList(res.items);
            setRefreshEscalationStatus(refreshEscalationStatus * 5);
        }).catch((error) => {
            setRefreshEscalationStatus(refreshEscalationStatus * 5);
            toasterUtils.showError(error);
        });
    };

    const getSearchFilters = (page: number, pageSize: number, sortColumn: string, sortDirection: number) => {
        var inputData: JobPostingSearchInputModel = {
            userId: "",
            companyId: "",
            jobCode: jobCodeSearch === "All" ? "" : jobCodeSearch,
            statusId: statusSearch === 0 ? null : statusSearch,
            primarySkillId: primarySkillSearch === 0 ? null : primarySkillSearch,
            page: page,
            pageSize: pageSize,
            sort: sortColumn,
            sortDir: sortDirection
        };
        return inputData;
    };

    const getJobPostings = (inputData: JobPostingSearchInputModel) => {
        setRefreshJobPosting(2);
        if (loggedInUser?.profileId) {
            inputData.userId = loggedInUser?.profileId;
            inputData.companyId = loggedInUser?.profileId;
            modalUtils.showLoader();
            companyDashboardService.getJobPostingsDashboard(inputData).then((res) => {
                modalUtils.closeModal();
                setJobPostingList([]);
                setJobPostingPagedList(res);
                setJobPostingList(res.items);
                setRefreshJobPosting(refreshJobPosting * 5);
            }).catch((error) => {
                modalUtils.closeModal();
                toasterUtils.showError(error);
            });
        }
    };

    const onJobStatusChangeActionHandler = (jobPosting: JobPostingStatusUpdateModel) => {
        jobPosting.assignToId = GuidEnum.Empty;
        const config: GeneralPoupConfig = {
            title: "Update Status",
            content: (
                <JobPostingStatusUpdatePopUp
                    objStatusUpdate={jobPosting}
                    statusList={status}
                    recruiterList={recruiterList}
                    onCloseStatusUpdate={onCloseStatusUpdateHandler}
                    onStatusUpdateSave={onStatusUpdateHandler}
                />
            ),
            size: ModalSize.LG,
            // className: "model-min-height-500"
        };
        modalUtils.showPopup(config);
    };

    const onCloseStatusUpdateHandler = () => {
        modalUtils.closeModal();
    };

    const onStatusUpdateHandler = (objJobPosting: JobPostingStatusUpdateModel) => {
        setRefreshJobPosting(2);
        modalUtils.showLoader();
        jobPostingService.updateJobPostingStatus(objJobPosting).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            toasterUtils.showSuccess(res.informationMessages.toString());
            getJobPostings(getSearchFilters(page, pageSize, sortColumn, sortDirection));
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onPageChangeActionHandler = (newPage: number) => {
        setPage(newPage);
        getJobPostings(getSearchFilters(newPage, pageSize, sortColumn, sortDirection));
    };

    const onPageSizeChangeActionHandler = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(PagingEnum.page);
        getJobPostings(getSearchFilters(PagingEnum.page, newPageSize, sortColumn, sortDirection));
    };

    return (
        <div id="main-wrapper">
            <div className="apply-filter">
                Filter Postings <i className="fas fa-filter" data-bs-toggle="offcanvas" data-bs-target="#offcanvasRight" aria-controls="offcanvasRight"></i>
            </div>
            <div className="offcanvas offcanvas-end" id="offcanvasRight" aria-labelledby="offcanvasRightLabel">
                <div className="offcanvas-header">
                    <h5 className="offcanvas-title" id="offcanvasRightLabel">Apply Filter</h5>
                    <a type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></a>
                </div>
                <div className="offcanvas-body">
                    <div className="row">
                        <div>
                            <FormControl fullWidth margin="none">
                                <Autocomplete options={primarySkills} value={primarySkills.find((x) => x.id === primarySkillSearch) || null}
                                    onChange={(_, newValue) => setPrimarySkillSearch(newValue ? newValue.id : 0)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Primary Skill" size="small" margin="normal" fullWidth />
                                    )}
                                />
                            </FormControl>
                        </div>
                        <div>
                            <FormControl fullWidth margin="normal">
                                <Autocomplete options={jobPostingList} value={jobPostingList.find((x) => x.jobCode === jobCodeSearch) || null}
                                    onChange={(_, newValue) => setJobCodeSearch(newValue ? newValue.jobCode : "All")} getOptionLabel={(option) => option?.jobCode || ""} isOptionEqualToValue={(option, value) => option.jobCode === value.jobCode}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Job Code" size="small" margin="normal" fullWidth />
                                    )}
                                />
                            </FormControl>
                        </div>
                        <div>
                            <FormControl fullWidth margin="normal">
                                <Autocomplete options={status} value={status.find((x) => x.id === statusSearch) || null}
                                    onChange={(_, newValue) => setStatusSearch(newValue ? newValue.id : 0)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Status" size="small" margin="normal" fullWidth />
                                    )}
                                />
                            </FormControl>
                        </div>
                        <div className="text-center">
                            <Button variant="contained" color="primary" type="submit"
                                onClick={() => getJobPostings(getSearchFilters(page, pageSize, sortColumn, sortDirection))} startIcon={<SearchIcon />}>
                                Search
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            {refreshJobPosting > 2 && status.length > 0 &&
                <JobPostingDashboardGridComponent
                    key={refreshJobPosting}
                    jobPostingList={jobPostingList}
                    status={status}
                    totalSize={jobPostingPagedList?.totalCount ? jobPostingPagedList?.totalCount : jobPostingList.length}
                    currentPage={page + 1}
                    pagesize={pageSize}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection === SortDirection.Asc ? "asc" : "desc"}
                    pageSizesList={pageSizesList}
                    escalationStatusList={escalationStatusList}
                    onJobStatusChangeAction={onJobStatusChangeActionHandler}
                    onPageChangeAction={onPageChangeActionHandler}
                    onPageSizeChangeAction={onPageSizeChangeActionHandler} />
            }
        </div>
    );
};
export default JobPostingDashboardComponent;
